<template>
  <!--
    HeroSection — the full-bleed, above-the-fold hero of the Home_Page
    (Requirement 4.1). It renders:
      - full-bleed background imagery with descriptive alt text,
      - an innovation headline (plus eyebrow/subhead) conveying what
        ThinkSpatial does, and
      - at least one CTA (a primary enquiry CTA, with an optional secondary).

    Copy and imagery come from the structured Home content module
    (client/src/content/home.js) so changes need no redesign (Requirement 11.2).
  -->
  <section class="hero" aria-labelledby="hero-headline">
    <!-- Full-bleed background image. Marked decorative on the layer itself and
         described via the visually-hidden <img> below so screen-reader users
         still receive the alt text (Requirement 9.3). -->
    <div
      class="hero__media"
      :style="{ backgroundImage: `url(${hero.image.src})` }"
      role="presentation"
    ></div>
    <img :src="hero.image.src" :alt="hero.image.alt" class="hero__a11y-img" />

    <!-- Dark scrim improves text contrast over imagery (Requirement 9.4). -->
    <div class="hero__scrim" aria-hidden="true"></div>

    <v-container class="hero__inner">
      <div class="hero__content">
        <p v-if="hero.eyebrow" class="hero__eyebrow">{{ hero.eyebrow }}</p>

        <h1 id="hero-headline" class="hero__headline">{{ hero.headline }}</h1>

        <p v-if="hero.subhead" class="hero__subhead">{{ hero.subhead }}</p>

        <div class="hero__actions">
          <CtaButton
            v-if="hero.primaryCta"
            :label="hero.primaryCta.label"
            :to="hero.primaryCta.to"
          />
          <CtaButton
            v-if="hero.secondaryCta"
            :label="hero.secondaryCta.label"
            :to="hero.secondaryCta.to"
            color="white"
            variant="outlined"
          />
        </div>
      </div>
    </v-container>
  </section>
</template>

<script>
// HeroSection — Home_Page hero (Requirement 4.1).
import CtaButton from 'components/CtaButton.vue'
import home from 'src/content/home'

export default {
  name: 'HeroSection',
  components: { CtaButton },
  data () {
    return {
      // Hero content slice of the structured Home content module.
      hero: home.hero
    }
  }
}
</script>

<style scoped>
.hero {
  position: relative;
  /* Full-bleed: span the entire viewport width regardless of container. */
  width: 100vw;
  left: 50%;
  right: 50%;
  margin-left: -50vw;
  margin-right: -50vw;
  min-height: 70vh;
  display: flex;
  align-items: center;
  overflow: hidden;
  color: #fff;
}

.hero__media {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  z-index: 0;
}

/* Visually-hidden image carrying the alt text for assistive tech. */
.hero__a11y-img {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
}

.hero__scrim {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    90deg,
    rgba(65, 40, 27, 0.78) 0%,
    rgba(65, 40, 27, 0.45) 55%,
    rgba(65, 40, 27, 0.2) 100%
  );
  z-index: 1;
}

.hero__inner {
  position: relative;
  z-index: 2;
}

.hero__content {
  max-width: 720px;
  padding: 48px 0;
}

.hero__eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-weight: 700;
  font-size: 0.85rem;
  margin-bottom: 12px;
  color: #f29400;
}

.hero__headline {
  font-size: clamp(2.2rem, 5vw, 3.6rem);
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 20px;
}

.hero__subhead {
  font-size: clamp(1rem, 2vw, 1.25rem);
  line-height: 1.6;
  margin-bottom: 32px;
  max-width: 56ch;
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}
</style>
